import Image from "next/image"
import Link from "next/link"
import type { ReactNode } from "react"
import { euros } from "@/lib/format"

// Image d'une création ou d'une page : versions optimisées adaptées à l'écran.
export function Photo({
  src,
  alt,
  sizes = "(max-width: 640px) 100vw, 50vw",
  className = "",
  priority = false,
  ratio = "aspect-[4/5]",
}: {
  src: string | null | undefined
  alt: string
  sizes?: string
  className?: string
  priority?: boolean
  ratio?: string
}) {
  return (
    <div className={`relative overflow-hidden bg-creme/60 ${ratio} ${className}`}>
      {src ? (
        <Image src={src} alt={alt} fill sizes={sizes} priority={priority} quality={80} className="object-cover" />
      ) : (
        <div className="absolute inset-0 grid place-items-center bg-creme/70">
          <Image src="/marque/renard-logo.webp" alt="" width={194} height={259} className="w-1/3 max-w-24 h-auto opacity-50" />
        </div>
      )}
    </div>
  )
}

export function Filet({ className = "" }: { className?: string }) {
  return <div aria-hidden className={`filet ${className}`} />
}

export function SectionTitle({ surtitre, children, id }: { surtitre?: string; children: ReactNode; id?: string }) {
  return (
    <div className="mb-6 text-center">
      {surtitre && <p className="surtitre mb-1">{surtitre}</p>}
      <h2 id={id} className="text-2xl sm:text-3xl">
        {children}
      </h2>
      <Filet className="mt-3" />
    </div>
  )
}

export function Prix({ value, className = "" }: { value: number | null | undefined; className?: string }) {
  return <span className={`font-bold text-brun tabular-nums ${className}`}>{euros(value)}</span>
}

export function Note({
  children,
  tone = "info",
  className = "",
}: {
  children: ReactNode
  tone?: "info" | "alerte" | "succes" | "neutre"
  className?: string
}) {
  const tones = {
    info: "bg-creme/70 border-ocre",
    alerte: "bg-rose-pale border-alerte text-brun-fonce",
    succes: "bg-[#eef4d6] border-vert",
    neutre: "bg-papier border-ligne",
  }
  return (
    <div role={tone === "alerte" ? "alert" : undefined} className={`border-l-4 rounded-r-xl px-4 py-3 text-[0.95rem] leading-relaxed ${tones[tone]} ${className}`}>
      {children}
    </div>
  )
}

export function Breadcrumb({ items }: { items: { href?: string; label: string }[] }) {
  return (
    <nav aria-label="Fil d’Ariane" className="text-sm text-brun-doux mb-4">
      {items.map((it, i) => (
        <span key={i}>
          {i > 0 && <span className="mx-1.5">›</span>}
          {it.href ? (
            <Link href={it.href} className="hover:text-brun underline-offset-2 hover:underline">
              {it.label}
            </Link>
          ) : (
            <span aria-current="page">{it.label}</span>
          )}
        </span>
      ))}
    </nav>
  )
}

export function PageHeading({ surtitre, title, children }: { surtitre?: string; title: string; children?: ReactNode }) {
  return (
    <header className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
      {surtitre && <p className="surtitre mb-2">{surtitre}</p>}
      <h1 className="text-3xl sm:text-4xl">{title}</h1>
      <Filet className="mt-4" />
      {children && <div className="mt-4 text-brun-doux prose-chaumiere">{children}</div>}
    </header>
  )
}
